
import React, { useState } from 'react';
import { Project, StudyType, TaskFrequency, CollectionMode } from '../types';
import { XIcon } from './icons';
import { VOICE_GUIDED_ENABLED } from '../utils/voiceGuided';

interface AddProjectModalProps {
  onClose: () => void;
  onCreateProject: (projectData: Omit<Project, 'id'>) => void;
}

const STUDY_TYPES: { value: StudyType; label: string }[] = [
  { value: 'diary', label: 'Diary Study' },
  { value: 'ethnography', label: 'Ethnography' },
  { value: 'ux', label: 'UX Research' },
  { value: 'brand', label: 'Brand Study' },
  { value: 'cultural_probe', label: 'Cultural Probe' },
  { value: 'custom', label: 'Custom' },
];

/* 'weekly'/'event' hidden 2026-07-30: labels only, no behavior behind them
   (the sole consumer is taskFrequency === 'one_time' → guided mode). The
   TaskFrequency union keeps them so existing projects stay valid. */
const TASK_FREQUENCIES: { value: TaskFrequency; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'one_time', label: 'One-time' },
];

const MEDIA_TYPES = ['photo', 'video', 'audio', 'text', 'screen', 'signification'] as const;

const COLLECTION_MODES: { value: CollectionMode; label: string; desc: string }[] = [
  { value: 'classic', label: 'Classic', desc: 'Standard task completion' },
  { value: 'pulse', label: 'Pulse', desc: 'Stimulus-response' },
  { value: 'journal', label: 'Journal', desc: 'Longitudinal tracking' },
];

const inputCls = 'w-full bg-white border border-app-border rounded-lg focus:outline-none focus:border-action focus:ring-1 focus:ring-action text-sm px-3 py-2';
const labelCls = 'block text-xs font-medium text-text-muted mb-1';
const sectionCls = 'text-[11px] font-semibold text-text-muted uppercase tracking-[1.5px] mb-3';

const AddProjectModal: React.FC<AddProjectModalProps> = ({ onClose, onCreateProject }) => {
  // Essentials
  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [clientName, setClientName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Participant experience
  const [welcomeTitle, setWelcomeTitle] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [consentText, setConsentText] = useState('');
  const [maxParticipants, setMaxParticipants] = useState('');

  // Study structure
  const [studyType, setStudyType] = useState<StudyType | ''>('');
  const [taskFrequency, setTaskFrequency] = useState<TaskFrequency | ''>('');
  const [anonymousResponses, setAnonymousResponses] = useState(false);
  const [participantUiMode, setParticipantUiMode] = useState<'standard' | 'voice_guided'>('standard');

  // Collection defaults
  const [defaultMediaTypes, setDefaultMediaTypes] = useState<Set<string>>(new Set(['photo', 'audio', 'text']));
  const [defaultCollectionMode, setDefaultCollectionMode] = useState<CollectionMode>('classic');

  const isFormValid = projectName.trim() !== '';

  const toggleMediaType = (type: string) => {
    setDefaultMediaTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;
    onCreateProject({
      name: projectName,
      description: description || undefined,
      client: clientName || undefined,
      welcomeTitle: welcomeTitle || `Welcome to ${projectName}`,
      welcomeMessage: welcomeMessage || "We're excited to have you participate. Your contributions are incredibly valuable.",
      tags: [],
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
      ...(consentText ? { consentText } : {}),
      ...(maxParticipants ? { maxParticipants: parseInt(maxParticipants, 10) } : {}),
      ...(studyType ? { studyType } : {}),
      ...(taskFrequency ? { taskFrequency } : {}),
      ...(anonymousResponses ? { anonymousResponses } : {}),
      ...(participantUiMode !== 'standard' ? { participantUiMode } : {}),
      ...(defaultMediaTypes.size > 0 ? { defaultMediaTypes: Array.from(defaultMediaTypes) } : {}),
      defaultCollectionMode,
    } as Omit<Project, 'id'>);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 p-4 flex items-center justify-center" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          {/* Header */}
          <div className="px-6 py-4 border-b border-app-border flex items-center justify-between flex-shrink-0">
            <h2 className="text-lg font-bold text-text-ink">Create New Project</h2>
            <button type="button" onClick={onClose} className="p-1 rounded-full text-text-muted hover:bg-neutral-100 active:bg-neutral-200">
              <XIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="p-6 space-y-6 overflow-y-auto flex-1 min-h-0">
            {/* ── Section 1: Essentials ── */}
            <div>
              <div className={sectionCls}>Essentials</div>
              <div className="space-y-3">
                <div>
                  <label htmlFor="pm-name" className={labelCls}>Project Name *</label>
                  <input type="text" id="pm-name" value={projectName} onChange={e => setProjectName(e.target.value)} className={inputCls} required placeholder="E.g., Commuter Experience Study" />
                </div>
                <div>
                  <label htmlFor="pm-desc" className={labelCls}>Description</label>
                  <textarea id="pm-desc" value={description} onChange={e => setDescription(e.target.value)} className={inputCls} rows={2} placeholder="Brief summary shown to participants" />
                </div>
                <div>
                  <label htmlFor="pm-client" className={labelCls}>Client / Organisation</label>
                  <input type="text" id="pm-client" value={clientName} onChange={e => setClientName(e.target.value)} className={inputCls} placeholder="E.g., City Transit Authority" />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label htmlFor="pm-start" className={labelCls}>Start Date</label>
                    <input type="date" id="pm-start" value={startDate} onChange={e => setStartDate(e.target.value)} className={inputCls} />
                  </div>
                  <div className="flex-1">
                    <label htmlFor="pm-end" className={labelCls}>End Date</label>
                    <input type="date" id="pm-end" value={endDate} onChange={e => setEndDate(e.target.value)} min={startDate || undefined} className={inputCls} />
                  </div>
                </div>
              </div>
            </div>

            {/* ── Section 2: Participant Experience ── */}
            <div>
              <div className={sectionCls}>Participant Experience</div>
              <div className="space-y-3">
                <div>
                  <label htmlFor="pm-wtitle" className={labelCls}>Welcome Title</label>
                  <input type="text" id="pm-wtitle" value={welcomeTitle} onChange={e => setWelcomeTitle(e.target.value)} className={inputCls} placeholder={`Welcome to ${projectName || 'your study'}`} />
                </div>
                <div>
                  <label htmlFor="pm-wmsg" className={labelCls}>Welcome Message</label>
                  <textarea id="pm-wmsg" value={welcomeMessage} onChange={e => setWelcomeMessage(e.target.value)} className={inputCls} rows={3} placeholder="Shown to participants during onboarding" />
                </div>
                <div>
                  <label htmlFor="pm-consent" className={labelCls}>Consent Text</label>
                  <textarea id="pm-consent" value={consentText} onChange={e => setConsentText(e.target.value)} className={inputCls} rows={3} placeholder="Participants agree to this before joining (leave blank to skip)" />
                </div>
                <div>
                  <label htmlFor="pm-maxp" className={labelCls}>Max Participants</label>
                  <input type="number" id="pm-maxp" value={maxParticipants} onChange={e => setMaxParticipants(e.target.value)} className={inputCls} min="1" placeholder="No limit" />
                </div>
              </div>
            </div>

            {/* ── Section 3: Study Structure ── */}
            <div>
              <div className={sectionCls}>Study Structure</div>
              <div className="space-y-3">
                {/* Study Type dropdown hidden 2026-07-29 — saved but unread.
                    Restore when the field drives behavior. */}
                <div>
                  <label htmlFor="pm-freq" className={labelCls}>Task Frequency</label>
                  <select id="pm-freq" value={taskFrequency} onChange={e => setTaskFrequency(e.target.value as TaskFrequency | '')} className={inputCls}>
                    <option value="">Select...</option>
                    {TASK_FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                {VOICE_GUIDED_ENABLED && (
                <div>
                  <label htmlFor="pm-uimode" className={labelCls}>Participant Experience</label>
                  <select id="pm-uimode" value={participantUiMode} onChange={e => setParticipantUiMode(e.target.value as 'standard' | 'voice_guided')} className={inputCls}>
                    <option value="standard">Standard</option>
                    <option value="voice_guided">Voice-guided (low literacy)</option>
                  </select>
                </div>
                )}
                {/* Anonymous Responses checkbox hidden 2026-07-30: the flag was
                    saved but read by nothing (broken promise). Plan to implement
                    is in docs/ARCHITECTURE-OVERVIEW.md §10. State wiring kept. */}
              </div>
            </div>

            {/* ── Section 4: Collection Defaults ── */}
            <div>
              <div className={sectionCls}>Collection Defaults</div>
              <div className="space-y-3">
                <div>
                  <label className={labelCls}>Default Media Types</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {MEDIA_TYPES.map(type => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => toggleMediaType(type)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          defaultMediaTypes.has(type)
                            ? 'bg-action/10 border-action text-action'
                            : 'bg-white border-app-border text-text-muted hover:border-text-muted'
                        }`}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Default Collection Mode</label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {COLLECTION_MODES.map(mode => (
                      <button
                        key={mode.value}
                        type="button"
                        onClick={() => setDefaultCollectionMode(mode.value)}
                        className={`p-2.5 rounded-lg border text-left transition-colors ${
                          defaultCollectionMode === mode.value
                            ? 'bg-action/10 border-action'
                            : 'bg-white border-app-border hover:border-text-muted'
                        }`}
                      >
                        <div className="text-xs font-semibold text-text-ink">{mode.label}</div>
                        <div className="text-[10px] text-text-muted mt-0.5">{mode.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-app-bg rounded-b-2xl flex justify-end gap-3 flex-shrink-0 border-t border-app-border">
            <button
              type="button"
              onClick={onClose}
              className="bg-white border border-app-border text-text-ink text-sm font-medium py-2 px-4 rounded-lg hover:bg-app-bg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid}
              className="bg-action text-white text-sm font-medium py-2 px-4 rounded-lg hover:bg-action/90 active:scale-95 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create Project
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProjectModal;
